import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import MathText from '../components/MathText';

const API = process.env.REACT_APP_API_URL || '';

export default function Practice() {
  const { skill } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const domain = location.state?.domain || '';

  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null); // { correct, correctAnswer, explanation }
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/learn/practice-questions?skill=${encodeURIComponent(skill)}`, {
      credentials: 'include',
    })
      .then(r => r.json())
      .then(data => { setQuestions(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [skill]);

  // Keyboard shortcuts A/B/C/D + Enter
  useEffect(() => {
    const handler = (e) => {
      if (done || loading) return;
      const key = e.key.toUpperCase();
      if (['A', 'B', 'C', 'D'].includes(key) && !result) {
        setSelected(key);
      }
      if (e.key === 'Enter') {
        if (result) {
          handleNext();
        } else if (selected) {
          handleSubmit(selected);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const handleSubmit = async (ans) => {
    const answer = ans ?? selected;
    if (!answer) return;
    const q = questions[idx];
    const res = await fetch(`${API}/api/learn/practice-answer`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ questionId: q.id, answer }),
    }).then(r => r.json());
    setResult(res);
    if (res.correct) setScore(s => s + 1);
  };

  const handleNext = () => {
    if (idx + 1 >= questions.length) {
      setDone(true);
    } else {
      setIdx(i => i + 1);
      setSelected(null);
      setResult(null);
    }
  };

  const handleRestart = () => {
    setIdx(0);
    setSelected(null);
    setResult(null);
    setScore(0);
    setDone(false);
  };

  const q = questions[idx];
  const progress = questions.length > 0 ? Math.round(((idx + (result ? 1 : 0)) / questions.length) * 100) : 0;

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '28px 24px 60px' }}>

      {/* Back + header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button
          className="btn btn-ghost btn-sm"
          style={{ color: 'var(--cb-gray-500)', padding: '6px 10px', fontSize: 13 }}
          onClick={() => navigate('/learn')}
        >
          ← Back
        </button>
        <div>
          <div style={{ fontSize: 11, color: 'var(--cb-gray-500)', marginBottom: 1 }}>{domain}</div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: 'var(--cb-navy-text)' }}>{skill}</h1>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="lb-block" style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--cb-gray-500)' }}>
          Loading questions…
        </div>
      )}

      {/* No questions */}
      {!loading && questions.length === 0 && (
        <div className="lb-block" style={{ padding: '40px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📭</div>
          <div style={{ fontWeight: 600, color: 'var(--cb-navy-text)', marginBottom: 6 }}>No questions available</div>
          <div style={{ fontSize: 13, color: 'var(--cb-gray-500)', marginBottom: 20 }}>
            No practice questions are loaded for this skill yet.
          </div>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/learn')}>← Back to Learn</button>
        </div>
      )}

      {/* Done screen */}
      {!loading && done && (
        <div className="lb-block" style={{ padding: '48px 32px', textAlign: 'center' }}>
          <div style={{ fontSize: 52, marginBottom: 16 }}>
            {score === questions.length ? '🏆' : score >= questions.length * 0.7 ? '🎉' : '📚'}
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--cb-navy-text)', marginBottom: 8 }}>
            Practice Complete
          </h2>
          <div style={{ fontSize: 36, fontWeight: 800, color: 'var(--cb-navy-text)', marginBottom: 4 }}>
            {score} / {questions.length}
          </div>
          <div style={{ fontSize: 14, color: 'var(--cb-gray-500)', marginBottom: 32 }}>
            {score === questions.length
              ? 'Perfect score! Great work.'
              : score >= questions.length * 0.7
              ? 'Nice work — keep it up.'
              : 'Keep practicing and you\'ll get there.'}
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={handleRestart}>Practice Again</button>
            <button className="btn btn-outline" onClick={() => navigate('/learn')}>← Back to Learn</button>
          </div>
        </div>
      )}

      {/* Question card */}
      {!loading && !done && q && (
        <div>
          {/* Progress bar */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--cb-gray-500)', marginBottom: 6 }}>
              <span>Question {idx + 1} of {questions.length}</span>
              <span>{score} correct</span>
            </div>
            <div style={{ height: 4, background: 'var(--cb-gray-200)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ width: `${progress}%`, height: '100%', background: 'var(--cb-blue)', borderRadius: 2, transition: 'width .3s ease' }} />
            </div>
          </div>

          <div className="lb-block">
            {/* Section/domain badge */}
            <div style={{ padding: '14px 24px 0', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <span className={`question-section-badge${q.section === 'Math' ? ' math' : ''}`}>{q.section}</span>
              {q.domain && <span className="question-domain" style={{ alignSelf: 'center' }}>{q.domain}</span>}
            </div>

            <div style={{ padding: '16px 24px 24px' }}>
              {/* Figure */}
              {q.figure && (
                <img src={`/figures/${q.figure}`} alt="figure"
                  style={{ maxWidth: '100%', marginBottom: 16, borderRadius: 8,
                    border: '1px solid var(--cb-gray-200)', display: 'block' }} />
              )}

              {/* Passage */}
              {q.passage && (
                <div style={{
                  background: 'var(--cb-gray-100)', borderRadius: 8, padding: '14px 16px',
                  fontSize: 14, lineHeight: 1.75, color: 'var(--cb-gray-700)', marginBottom: 18,
                  borderLeft: '3px solid var(--cb-gray-300)',
                }}>
                  <MathText text={q.passage} />
                </div>
              )}

              {/* Question text */}
              <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--cb-navy-text)', marginBottom: 18, lineHeight: 1.55 }}>
                <MathText text={q.question} />
              </p>

              {/* Choices */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 22 }}>
                {Object.entries(q.choices).map(([letter, text]) => {
                  let cls = 'choice-btn';
                  if (result) {
                    if (letter === result.correctAnswer) cls += ' correct';
                    else if (letter === selected && !result.correct) cls += ' incorrect';
                  } else if (letter === selected) {
                    cls += ' selected';
                  }
                  return (
                    <button key={letter} className={cls} disabled={!!result}
                      onClick={() => { if (!result) { setSelected(letter); handleSubmit(letter); } }}>
                      <span className="choice-label" style={{ fontWeight: 700, marginRight: 10 }}>{letter}</span>
                      <MathText text={text} />
                    </button>
                  );
                })}
              </div>

              {/* Explanation */}
              {result && (
                <div style={{
                  background: result.correct ? 'var(--cb-green-light)' : 'var(--cb-red-light)',
                  border: `1px solid ${result.correct ? 'var(--cb-green)' : 'var(--cb-red)'}`,
                  borderRadius: 8, padding: '14px 18px', marginBottom: 22,
                }}>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6,
                    color: result.correct ? 'var(--cb-green)' : 'var(--cb-red)' }}>
                    {result.correct ? '✓ Correct!' : `✗ Incorrect — the answer is ${result.correctAnswer}`}
                  </div>
                  {result.explanation && (
                    <div style={{ fontSize: 14, lineHeight: 1.65, color: 'var(--cb-gray-700)' }}>
                      <MathText text={result.explanation} />
                    </div>
                  )}
                </div>
              )}

              {/* Navigation */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 12, color: 'var(--cb-gray-500)' }}>
                  {!result && 'Click a choice or press A / B / C / D'}
                  {result && (
                    <span>Press <kbd style={{ background: 'var(--cb-gray-200)', padding: '1px 5px', borderRadius: 3, fontSize: 11 }}>Enter</kbd> for next</span>
                  )}
                </div>
                {result && (
                  <button className="btn btn-primary" onClick={handleNext}>
                    {idx + 1 >= questions.length ? 'Finish' : 'Next →'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
